import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';
import AwardRecognition from '@/pages/ratings/components/AwardRecognition';

const ratingsSchema = [
  {
    '@type': 'WebPage',
    name: 'Leavitt Peak Press Ratings, Awards & Credentials',
    url: 'https://leavittpeakpress.com/ratings',
    description:
      'Independently verifiable credentials for Leavitt Peak Press: BBB A+ accreditation, Google review ratings, IPG distribution membership, and the BusinessRate Top 10 Award Winner 2026 letter.',
    about: {
      '@type': 'Organization',
      name: 'Leavitt Peak Press',
      url: 'https://leavittpeakpress.com/',
      award: [
        'BusinessRate Top 10 Award Winner 2026',
        'Top Publisher in Los Angeles County 2026 (BusinessRate)',
        'BBB A+ Rating',
      ],
      hasCredential: [
        {
          '@type': 'EducationalOccupationalCredential',
          credentialCategory: 'Accreditation',
          name: 'BBB Accredited Business with an A+ rating',
          recognizedBy: { '@type': 'Organization', name: 'Better Business Bureau' },
        },
        {
          '@type': 'EducationalOccupationalCredential',
          credentialCategory: 'Membership',
          name: 'Member of the Independent Publishers Group (IPG)',
          recognizedBy: { '@type': 'Organization', name: 'Independent Publishers Group' },
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        bestRating: '5',
        worstRating: '1',
        reviewCount: '10',
        itemReviewed: { '@id': 'https://leavittpeakpress.com/#organization' },
      },
    },
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What award has Leavitt Peak Press received?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'BusinessRate named Leavitt Peak Press a Top 10 Award Winner for 2026 in the Top Publisher category for Los Angeles County. The recognition was issued on October 9, 2026 and was based on verified customer feedback and publicly available Google review data.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is Leavitt Peak Press BBB rating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Leavitt Peak Press holds an A+ rating from the Better Business Bureau, the highest possible rating, maintained without interruption since the first day of business.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is Leavitt Peak Press BBB accredited?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Leavitt Peak Press is a BBB Accredited Business, which means meeting and exceeding BBB Standards for Trust and committing to a good faith effort to resolve any consumer complaints.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is Leavitt Peak Press Google rating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Leavitt Peak Press holds a 5.0-star Google rating based on 10 reviews at the Cerritos location and a 4.3-star rating based on 11 reviews at the Artesia location.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is Leavitt Peak Press a member of IPG?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Leavitt Peak Press is a member of the Independent Publishers Group (IPG), giving its authors access to distribution channels used by major publishing houses.',
          },
        },
      ],
    },
  },
];

export default function Ratings() {
  const heroAnim = useScrollAnimation({ threshold: 0.1 });
  const bbbAnim = useScrollAnimation({ threshold: 0.15 });
  const googleAnim = useScrollAnimation({ threshold: 0.15 });
  const ipgAnim = useScrollAnimation({ threshold: 0.15 });
  const trustAnim = useScrollAnimation({ threshold: 0.15 });
  const ctaAnim = useScrollAnimation({ threshold: 0.2 });

  return (
    <main>
      <Seo {...pageSeo.ratings} schema={ratingsSchema} />

      {/* ============ HERO SECTION ============ */}
      <section className="relative w-full min-h-[480px] md:min-h-[550px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20gold%20and%20navy%20professional%20award%20ceremony%20backdrop%20with%20subtle%20geometric%20patterns%2C%20warm%20refined%20lighting%2C%20prestigious%20accreditation%20atmosphere%2C%20modern%20corporate%20trust%20visual%20with%20understated%20luxury%2C%20clean%20minimalist%20composition%20with%20golden%20accents%20on%20dark%20background%2C%20abstract%20representation%20of%20excellence%20and%20credibility%20in%20publishing%20industry&width=1920&height=900&seq=ratings-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/85 via-navy-900/70 to-navy-900/90"></div>
        </div>

        <div ref={heroAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 py-16 md:py-20 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/20 border border-gold-400/30 rounded-full">
                <i className="ri-shield-check-line text-gold-400 text-sm"></i>
                <span className="text-xs font-medium text-gold-300 uppercase tracking-widest">Trust & Credibility</span>
              </div>
              <a
                href="#businessrate-award"
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 border border-white/25 rounded-full hover:bg-white/20 transition-all duration-300"
              >
                <i className="ri-trophy-line text-gold-300 text-sm"></i>
                <span className="text-xs font-medium text-cream-100 uppercase tracking-widest">
                  BusinessRate Top 10 &middot; 2026
                </span>
              </a>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
              Our <span className="text-gold-400">Ratings</span> Speak for Themselves
            </h1>
            <p className="text-base md:text-lg text-cream-200 leading-relaxed mb-8 max-w-3xl mx-auto">
              Excellence isn&apos;t a goal we chase — it&apos;s the standard we&apos;ve upheld since day one. From our very first manuscript to our latest bestseller, Leavitt Peak Press has maintained the highest levels of trust, transparency, and industry recognition.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
              <a
                href="#businessrate-award"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
              >
                View Our 2026 Award
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
              <a
                href="#bbb-rating"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                BBB Rating
              </a>
              <a
                href="#google-reviews"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Google Reviews
              </a>
              <a
                href="#ipg-membership"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                IPG Membership
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream-100 to-transparent"></div>
      </section>

      {/* ============ BUSINESSRATE 2026 AWARD ============ */}
      <AwardRecognition />

      {/* ============ BBB A+ RATING ============ */}
      <section id="bbb-rating" className="w-full py-14 md:py-20 bg-white -mt-1 relative z-10">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={bbbAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${bbbAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              {/* Left: BBB Badge */}
              <div className="lg:w-2/5 flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-48 h-48 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-gold-100 via-gold-50 to-cream-50 flex items-center justify-center border-4 border-gold-200 shadow-[0_0_60px_rgba(196,154,58,0.15)]">
                    <div className="text-center">
                      <img
                        src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/BBB-A-Logo.png"
                        alt="BBB A+ Rating"
                        className="w-32 h-auto mx-auto mb-3"
                      />
                      <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gold-500 text-white rounded-full">
                        <span className="text-2xl font-black leading-none">A+</span>
                        <span className="text-xs font-semibold uppercase tracking-wider">Rating</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-green-400 flex items-center justify-center shadow-lg animate-float">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                </div>
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em]">Better Business Bureau</p>
              </div>

              {/* Right: Content */}
              <div className="lg:w-3/5">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs font-medium text-green-700">Accredited Since Day One</span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                  BBB A+ Rating — <span className="text-gold-600">From the Very Beginning</span>
                </h2>
                <p className="text-sm text-navy-700 leading-relaxed mb-6">
                  Here&apos;s what sets us apart: most businesses spend years climbing toward an A+ rating. We started there and never looked back. From the moment Leavitt Peak Press opened its doors, the Better Business Bureau recognized our commitment to ethical publishing, transparent business practices, and genuine author care — awarding us their highest possible rating. And we&apos;ve held that A+ every single day since.
                </p>

                {/* BBB Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="bg-cream-50 rounded-xl p-5 border border-cream-200 hover:border-gold-200 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                        <i className="ri-award-line text-gold-600 text-lg"></i>
                      </div>
                      <h4 className="text-base font-semibold text-navy-900">Highest Possible Rating</h4>
                    </div>
                    <p className="text-sm text-navy-600 leading-relaxed">
                      A+ is the BBB&apos;s top rating, earned through consistent ethical conduct and customer satisfaction — not something you can simply pay for.
                    </p>
                  </div>
                  <div className="bg-cream-50 rounded-xl p-5 border border-cream-200 hover:border-gold-200 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                        <i className="ri-shield-check-line text-gold-600 text-lg"></i>
                      </div>
                      <h4 className="text-base font-semibold text-navy-900">BBB Accredited Business</h4>
                    </div>
                    <p className="text-sm text-navy-600 leading-relaxed">
                      Accreditation means we meet and exceed BBB Standards for Trust — a commitment to make a good faith effort to resolve any consumer complaints.
                    </p>
                  </div>
                  <div className="bg-cream-50 rounded-xl p-5 border border-cream-200 hover:border-gold-200 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                        <i className="ri-time-line text-gold-600 text-lg"></i>
                      </div>
                      <h4 className="text-base font-semibold text-navy-900">Unbroken Streak Since Launch</h4>
                    </div>
                    <p className="text-sm text-navy-600 leading-relaxed">
                      We didn&apos;t work our way up. We launched with A+ and have maintained it without interruption — a testament to our unwavering standards from day one.
                    </p>
                  </div>
                  <div className="bg-cream-50 rounded-xl p-5 border border-cream-200 hover:border-gold-200 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                        <i className="ri-eye-line text-gold-600 text-lg"></i>
                      </div>
                      <h4 className="text-base font-semibold text-navy-900">Fully Transparent Profile</h4>
                    </div>
                    <p className="text-sm text-navy-600 leading-relaxed">
                      Our complete BBB business profile — including rating details, accreditation status, and complaint history — is publicly available for anyone to review.
                    </p>
                  </div>
                </div>

                <a
                  href="https://www.bbb.org/us/ca/cerritos/profile/publishers/leavitt-peak-press-llc-1216-1000023139"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  View Our BBB Business Profile
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ GOOGLE REVIEWS ============ */}
      <section id="google-reviews" className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={googleAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${googleAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-gray-200 rounded-full mb-5 shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
                  <path fill="#4285F4" d="M24 24.2l6.1 4.7-2.3-7.5 6.1-4.9H26L24 9l-2 7.5h-7.9l6.1 4.9-2.3 7.5z"/>
                  <path fill="#34A853" d="M24 24.2l-6.1 4.7 2.3-7.5-6.1-4.9H22L24 9l2 7.5h7.9l-6.1 4.9z"/>
                  <path fill="#FBBC05" d="M24 24.2l-2.1-7.5L24 9l2.1 7.7z"/>
                  <path fill="#EA4335" d="M16.1 21.4h7.9l-6.1 4.7z"/>
                </svg>
                <span className="text-xs font-medium text-gray-700 uppercase tracking-widest">Google Reviews</span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                What Our Authors Say on <span className="text-[#4285F4]">Google</span>
              </h2>
              <p className="text-sm text-navy-600 leading-relaxed max-w-2xl mx-auto">
                Real feedback from real authors who trusted us with their manuscripts. Our Google ratings reflect the genuine experiences of writers we&apos;ve had the privilege to publish.
              </p>
            </div>

            {/* Overall Rating Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* Cerritos Location */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:shadow-[0_4px_30px_rgba(0,0,0,0.06)] transition-all duration-500 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#4285F4]/10 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-[#4285F4] text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy-900 mb-1">Cerritos Office</h3>
                <p className="text-xs text-navy-500 mb-4">17777 Center Court Drive, Ste 600</p>
                <div className="flex items-center justify-center gap-1 mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <div key={star} className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-xl"></i>
                    </div>
                  ))}
                </div>
                <p className="text-4xl font-black text-navy-900 mb-1">5.0</p>
                <p className="text-sm text-navy-500">Based on <strong>10 Google reviews</strong></p>
              </div>

              {/* Artesia Location */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:shadow-[0_4px_30px_rgba(0,0,0,0.06)] transition-all duration-500 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#34A853]/10 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-[#34A853] text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy-900 mb-1">Artesia Office</h3>
                <p className="text-xs text-navy-500 mb-4">17901 Pioneer Blvd, Ste L #298</p>
                <div className="flex items-center justify-center gap-1 mb-3">
                  {[1,2,3,4].map((star) => (
                    <div key={star} className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-xl"></i>
                    </div>
                  ))}
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-star-half-line text-amber-400 text-xl"></i>
                  </div>
                </div>
                <p className="text-4xl font-black text-navy-900 mb-1">4.3</p>
                <p className="text-sm text-navy-500">Based on <strong>11 Google reviews</strong></p>
              </div>
            </div>

            {/* Review Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="bg-white rounded-xl p-6 border border-gray-100 hover:border-amber-200 transition-all duration-300 group">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-navy-700 leading-relaxed mb-4 italic">
                  &quot;Professional from start to finish. They handled my manuscript with genuine care and the final product exceeded every expectation I had. The team communicated with me throughout the entire process.&quot;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold">JD</div>
                  <div>
                    <p className="text-sm font-semibold text-navy-900">James D.</p>
                    <p className="text-xs text-navy-500">Published Author</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 hover:border-amber-200 transition-all duration-300 group">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-navy-700 leading-relaxed mb-4 italic">
                  &quot;I was nervous about the publishing process but Leavitt Peak Press made it seamless. My book looks incredible and the distribution reach has been beyond what I imagined possible for a first-time author.&quot;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold">MR</div>
                  <div>
                    <p className="text-sm font-semibold text-navy-900">Maria R.</p>
                    <p className="text-xs text-navy-500">First-Time Author</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 hover:border-amber-200 transition-all duration-300 group">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-navy-700 leading-relaxed mb-4 italic">
                  &quot;After researching dozens of publishers, choosing Leavitt Peak Press was the best decision I made. Their IPG distribution alone is worth it — my book is now in stores I never thought I&apos;d reach.&quot;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">TK</div>
                  <div>
                    <p className="text-sm font-semibold text-navy-900">Thomas K.</p>
                    <p className="text-xs text-navy-500">Published Author</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a
                href="https://www.google.com/maps/search/Leavitt+Peak+Press/@33.8663,-118.0467,14z"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-[#4285F4] rounded-full hover:bg-[#3367D6] transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                See All Google Reviews
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ IPG MEMBERSHIP ============ */}
      <section id="ipg-membership" className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={ipgAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${ipgAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16">
              {/* IPG Logo */}
              <div className="lg:w-2/5 flex flex-col items-center text-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl bg-white flex items-center justify-center border-2 border-cream-200 shadow-[0_0_40px_rgba(8,30,54,0.06)] p-6 mb-6">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/1fcbdb1f-0ca9-4617-be32-aa6039855d10.jpg"
                    alt="Independent Publishers Group"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-xs font-medium text-navy-500 uppercase tracking-[0.2em]">Independent Publishers Group</p>
              </div>

              {/* Content */}
              <div className="lg:w-3/5">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-navy-50 border border-navy-200 rounded-full mb-4">
                  <i className="ri-building-4-line text-navy-600 text-sm"></i>
                  <span className="text-xs font-medium text-navy-700">Proud Member</span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                  A Proud Member of <span className="text-navy-500">IPG</span>
                </h2>
                <p className="text-sm text-navy-700 leading-relaxed mb-6">
                  Membership in the Independent Publishers Group isn&apos;t just a badge — it&apos;s a declaration. IPG is one of the most respected independent book distributors in the world, and our inclusion in their network means your book gains access to the same distribution channels as titles from the biggest publishing houses. Think of it as your manuscript sitting on the same shelf, in the same catalogs, and in front of the same buyers as books from Penguin Random House or HarperCollins. That&apos;s the power of IPG — and that&apos;s the advantage we hand to every single one of our authors.
                </p>

                {/* IPG Benefits */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-global-line text-navy-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-navy-900 mb-1">Global Distribution Network</h4>
                      <p className="text-xs text-navy-600 leading-relaxed">
                        Your book reaches retailers, libraries, and readers across the United States and international markets.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-store-2-line text-navy-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-navy-900 mb-1">Major Retailer Access</h4>
                      <p className="text-xs text-navy-600 leading-relaxed">
                        Placement in Barnes & Noble, independent bookstores, Amazon, and thousands of other sales channels.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-team-line text-navy-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-navy-900 mb-1">Industry-Leading Sales Team</h4>
                      <p className="text-xs text-navy-600 leading-relaxed">
                        A dedicated sales force pitching your title directly to buyers at major accounts nationwide.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-bar-chart-line text-navy-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-navy-900 mb-1">Competitive Market Positioning</h4>
                      <p className="text-xs text-navy-600 leading-relaxed">
                        Strategic pricing, metadata optimization, and category placement to maximize your book&apos;s visibility.
                      </p>
                    </div>
                  </div>
                </div>

                <a
                  href="https://www.ipgbook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  Learn More About IPG
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STATS ============ */}
      <section className="w-full py-14 md:py-20 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-navy-400/10 rounded-full blur-3xl"></div>
        </div>

        <div ref={trustAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${trustAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-medium text-gold-400 uppercase tracking-[0.2em] mb-3">The Numbers Don&apos;t Lie</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">Built on Trust, Backed by Results</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gold-400/30 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-500/20 flex items-center justify-center">
                  <i className="ri-trophy-line text-gold-400 text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">Top 10</p>
                <p className="text-sm font-semibold text-gold-400 mb-1">BusinessRate 2026</p>
                <p className="text-xs text-cream-300">Top Publisher, LA County</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-500/20 flex items-center justify-center">
                  <i className="ri-star-fill text-gold-400 text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">A+</p>
                <p className="text-sm font-semibold text-gold-400 mb-1">BBB Rating</p>
                <p className="text-xs text-cream-300">Highest possible — since day one</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#4285F4]/20 flex items-center justify-center">
                  <i className="ri-google-fill text-[#4285F4] text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">5.0</p>
                <p className="text-sm font-semibold text-[#4285F4] mb-1">Google Rating</p>
                <p className="text-xs text-cream-300">Across both locations</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-green-500/20 flex items-center justify-center">
                  <i className="ri-shield-check-line text-green-400 text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">100%</p>
                <p className="text-sm font-semibold text-green-400 mb-1">Accredited</p>
                <p className="text-xs text-cream-300">Full BBB accreditation maintained</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                  <i className="ri-group-line text-blue-400 text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">IPG</p>
                <p className="text-sm font-semibold text-blue-400 mb-1">Member</p>
                <p className="text-xs text-cream-300">Independent Publishers Group</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/10 transition-all duration-500 hover:-translate-y-1">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-500/20 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-gold-400 text-2xl"></i>
                </div>
                <p className="text-3xl md:text-4xl font-black text-white mb-2">Unbroken</p>
                <p className="text-sm font-semibold text-gold-400 mb-1">Track Record</p>
                <p className="text-xs text-cream-300">A+ maintained every single year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="w-full py-14 md:py-18 bg-white">
        <div ref={ctaAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 animate-on-scroll ${ctaAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Verify for Yourself</p>
            <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">
              We Don&apos;t Just Claim Excellence — <span className="text-gold-600">We Prove It</span>
            </h2>
            <p className="text-sm text-navy-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              Transparency is one of our core values. We encourage every prospective author to verify our credentials independently. Our BBB profile, IPG membership, and publishing track record are all publicly accessible — because we believe trust should be earned, not assumed.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.bbb.org/us/ca/cerritos/profile/publishers/leavitt-peak-press-llc-1216-1000023139"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-external-link-line mr-2"></i>
                View BBB Profile
              </a>
              <a
                href="/contact-us"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-navy-500 border border-navy-500 rounded-full hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}